<template>
  <div class="order-detail">
    <!-- 页面标题和返回按钮 -->
    <div class="page-header">
      <div class="header-left">
        <el-button class="back-btn" @click="goBack" :icon="ArrowLeft">返回订单列表</el-button>
        <div class="header-text">
          <h2 class="page-title">订单详情</h2>
          <p class="page-subtitle">订单号：{{ orderDetail.orderNo || '—' }}</p>
        </div>
      </div>
    </div>

    <div v-loading="loading">
      <!-- 所属活动信息（显眼区块，位于页面最上方） -->
      <el-card v-if="activityInfo" shadow="never" class="activity-card">
        <template #header>
          <div class="activity-card-header">
            <div class="header-icon">
              <el-icon :size="18"><Calendar /></el-icon>
            </div>
            <span class="header-title">所属活动</span>
            <span class="header-tip">本订单参与的活动信息</span>
          </div>
        </template>

        <div class="activity-body">
          <div class="activity-main">
            <h3 class="activity-title">{{ activityInfo.title }}</h3>
            <div class="activity-meta">
              <div class="meta-row">
                <span class="meta-label">举办时间</span>
                <span class="meta-value activity-time">{{ activityTimeText }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">举办场馆</span>
                <span class="meta-value">{{ venueText || '—' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">活动地点</span>
                <span class="meta-value">{{ activityInfo.location || '—' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">运动类型</span>
                <span class="meta-value">{{ activityInfo.type || activityInfo.sportType || '—' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">赛事格式</span>
                <span class="meta-value">{{ formatText }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">报名情况</span>
                <span class="meta-value">{{ activityInfo.participants || 0 }} / {{ activityInfo.maxParticipants || 0 }} 人</span>
              </div>
              <div class="meta-row" v-if="activityInfo.prize_amount > 0">
                <span class="meta-label">奖金池</span>
                <span class="meta-value">¥{{ Number(activityInfo.prize_amount).toFixed(2) }}</span>
              </div>
              <div class="meta-row" v-if="activityInfo.fee > 0">
                <span class="meta-label">报名费</span>
                <span class="meta-value">¥{{ Number(activityInfo.fee).toFixed(2) }}</span>
              </div>
            </div>
          </div>

          <div class="activity-side">
            <img
              v-if="activityInfo.cover_image"
              :src="getImageUrl(activityInfo.cover_image)"
              class="activity-cover"
              alt="活动封面"
            />
            <div class="activity-status">
              <el-tag :type="activityStatusTag" effect="light" size="large">{{ activityStatusText }}</el-tag>
            </div>
            <div class="activity-id">活动ID：{{ activityInfo.id }}</div>
          </div>
        </div>
      </el-card>

      <!-- 无关联活动提示（会员订单等） -->
      <el-card v-else shadow="never" class="activity-card no-activity-card">
        <div class="no-activity">
          <el-icon :size="28" color="#c0c4cc"><InfoFilled /></el-icon>
          <span>该订单不关联具体活动{{ sourceTable === 'membership' ? '（会员购买订单）' : '' }}</span>
        </div>
      </el-card>

      <!-- 订单信息 -->
      <el-card shadow="never" class="order-card">
        <template #header>
          <div class="card-header">
            <span>订单信息</span>
          </div>
        </template>

        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ orderDetail.orderNo || '—' }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">{{ orderTypeLabel }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag :type="statusTagType" effect="light" size="small">{{ statusLabel }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="支付方式">{{ paymentMethodLabel }}</el-descriptions-item>
          <el-descriptions-item label="订单金额">¥{{ orderDetail.totalAmount || 0 }}</el-descriptions-item>
          <el-descriptions-item label="优惠金额">¥{{ orderDetail.discountAmount || 0 }}</el-descriptions-item>
          <el-descriptions-item label="实付金额">¥{{ orderDetail.finalAmount || 0 }}</el-descriptions-item>
          <el-descriptions-item label="关联活动ID">{{ orderDetail.activityId || orderDetail.activity_id || '—' }}</el-descriptions-item>
          <el-descriptions-item label="下单用户">{{ orderDetail.username || '—' }}</el-descriptions-item>
          <el-descriptions-item label="用户手机号">{{ orderDetail.phone || '—' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDateTime(orderDetail.createTime) || '—' }}</el-descriptions-item>
          <el-descriptions-item label="支付时间">{{ formatDateTime(orderDetail.payTime) || '未支付' }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ formatDateTime(orderDetail.completeTime) || '未完成' }}</el-descriptions-item>
          <el-descriptions-item label="备注">{{ orderDetail.remark || '—' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 会员订单扩展信息 -->
        <div v-if="sourceTable === 'membership'" class="membership-section">
          <h4>会员套餐信息</h4>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="套餐">{{ orderDetail.plan || '—' }}</el-descriptions-item>
            <el-descriptions-item label="购买月数">{{ orderDetail.months || '—' }} 个月</el-descriptions-item>
            <el-descriptions-item label="到期时间">{{ formatDateTime(orderDetail.expire_time) || '—' }}</el-descriptions-item>
            <el-descriptions-item label="退款状态">{{ orderDetail.refund_status || '—' }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 订单商品（活动订单） -->
        <div v-if="orderDetail.items && orderDetail.items.length > 0" class="items-section">
          <h4>订单商品</h4>
          <el-table :data="orderDetail.items" size="small" border style="width: 100%">
            <el-table-column prop="name" label="商品名称" min-width="200" />
            <el-table-column prop="quantity" label="数量" width="80" />
            <el-table-column prop="price" label="单价" width="120">
              <template #default="{ row }">¥{{ row.price }}</template>
            </el-table-column>
            <el-table-column label="小计" width="120">
              <template #default="{ row }">¥{{ (Number(row.price) * Number(row.quantity)).toFixed(2) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'
import api from '@/utils/api'

export default {
  name: 'OrderDetail',

  setup() {
    const route = useRoute()
    const router = useRouter()

    const loading = ref(false)
    const orderDetail = ref({})
    const activityInfo = ref(null)
    const sourceTable = ref('activity')

    // 加载订单详情
    const loadOrderDetail = async () => {
      const orderId = route.params.id
      if (!orderId) {
        ElMessage.error('缺少订单ID')
        return
      }
      loading.value = true
      try {
        const response = await api.getOrderDetail(orderId)
        orderDetail.value = response.data.order || {}
        activityInfo.value = response.data.activity || null
        sourceTable.value = response.data.source_table || 'activity'
      } catch (error) {
        console.error('加载订单详情失败:', error)
        ElMessage.error(error.response?.data?.message || '加载订单详情失败')
      } finally {
        loading.value = false
      }
    }

    // 返回订单列表
    const goBack = () => {
      router.push('/orders')
    }

    // 活动时间文本（兼容 ISO / 已格式化时间，展示举办时间范围）
    const activityTimeText = computed(() => {
      const start = activityInfo.value?.startTime || activityInfo.value?.start_time
      const end = activityInfo.value?.endTime || activityInfo.value?.end_time
      if (!start) return '—'
      const startText = formatActivityTime(start)
      if (!end) return startText
      const endText = formatActivityTime(end)
      return endText && endText !== startText ? `${startText} 至 ${endText}` : startText
    })

    // 场馆名称（兼容蛇形/驼峰字段）
    const venueText = computed(() => {
      const a = activityInfo.value
      return (a && (a.venue_name || a.venueName)) || ''
    })

    // 赛事格式文本
    const formatText = computed(() => {
      const fmt = activityInfo.value?.tournament_format || activityInfo.value?.tournamentFormat
      const formatMap = {
        single_elimination: '单淘汰赛',
        round_robin: '循环赛',
        group_stage_then_knockout: '小组赛+淘汰赛'
      }
      return fmt ? (formatMap[fmt] || fmt) : '—'
    })

    // 活动状态
    const activityStatusText = computed(() => {
      const status = activityInfo.value?.status
      if (!status) return '—'
      const statusMap = {
        published: '已发布',
        upcoming: '即将开始',
        ongoing: '进行中',
        completed: '已结束',
        cancelled: '已取消',
        draft: '草稿',
        '进行中': '进行中',
        '已结束': '已结束'
      }
      return statusMap[status] || status
    })

    const activityStatusTag = computed(() => {
      const status = activityInfo.value?.status
      const tagMap = {
        published: 'success',
        upcoming: 'warning',
        ongoing: 'primary',
        completed: 'info',
        cancelled: 'danger',
        draft: 'info',
        '进行中': 'primary',
        '已结束': 'info'
      }
      return tagMap[status] || 'info'
    })

    // 订单类型
    const orderTypeLabel = computed(() => {
      const type = orderDetail.value.orderType || orderDetail.value.order_type
      const labels = {
        activity: '活动报名',
        activity_join: '活动报名',
        membership: '会员购买',
        sponsor: '赞助费',
        equipment: '装备购买'
      }
      return labels[type] || type || '—'
    })

    // 订单状态
    const statusLabel = computed(() => {
      const status = orderDetail.value.status
      const labels = {
        pending: '待支付',
        paid: '已支付',
        completed: '已完成',
        cancelled: '已取消',
        refunded: '已退款',
        expired: '已过期'
      }
      return labels[status] || status || '—'
    })

    const statusTagType = computed(() => {
      const status = orderDetail.value.status
      const types = {
        pending: 'warning',
        paid: 'success',
        completed: 'info',
        cancelled: 'danger',
        refunded: 'danger',
        expired: 'info'
      }
      return types[status] || 'info'
    })

    // 支付方式
    const paymentMethodLabel = computed(() => {
      const method = orderDetail.value.paymentMethod || orderDetail.value.payment_method
      const labels = {
        wechat: '微信支付',
        '微信支付': '微信支付',
        alipay: '支付宝',
        bank: '银行卡',
        balance: '余额支付',
        virtual: '虚拟支付'
      }
      return labels[method] || method || '—'
    })

    // 工具函数
    const formatDateTime = (datetime) => {
      if (!datetime) return ''
      // 兼容 ISO 与已格式化时间
      if (typeof datetime === 'string' && datetime.includes('T')) {
        return new Date(datetime).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      return datetime
    }

    const formatActivityTime = (datetime) => formatDateTime(datetime)

    const getImageUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http')) return url
      return url
    }

    onMounted(() => {
      loadOrderDetail()
    })

    return {
      ArrowLeft,
      loading,
      orderDetail,
      activityInfo,
      sourceTable,

      goBack,
      activityTimeText,
      venueText,
      formatText,
      activityStatusText,
      activityStatusTag,
      orderTypeLabel,
      statusLabel,
      statusTagType,
      paymentMethodLabel,
      formatDateTime,
      getImageUrl
    }
  }
}
</script>

<style scoped>
.order-detail {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.page-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #909399;
}

/* 所属活动卡片：统一卡片视觉 + 高亮边框使其更显眼 */
.activity-card {
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  border-left: 4px solid #409eff;
}

.activity-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #ecf5ff;
  color: #409eff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.header-tip {
  font-size: 12px;
  color: #c0c4cc;
}

.activity-body {
  display: flex;
  gap: 20px;
}

.activity-main {
  flex: 1;
  min-width: 0;
}

.activity-title {
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.activity-meta {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 32px;
}

.meta-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
}

.meta-label {
  flex-shrink: 0;
  width: 70px;
  font-size: 13px;
  color: #909399;
}

.meta-value {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  overflow-wrap: break-word;
}

.activity-time {
  color: #409eff;
  font-weight: 600;
}

.activity-side {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  width: 160px;
}

.activity-cover {
  width: 160px;
  height: 120px;
  border-radius: 8px;
  object-fit: cover;
  background: #f5f7fa;
}

.activity-status {
  display: flex;
  justify-content: flex-end;
}

.activity-id {
  font-size: 12px;
  color: #c0c4cc;
}

/* 无关联活动提示 */
.no-activity-card {
  border-left: 4px solid #e4e7ed;
}

.no-activity {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 28px 0;
  color: #909399;
  font-size: 14px;
}

/* 订单信息卡片 */
.order-card {
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.card-header {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.items-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e6e8eb;
}

.items-section h4,
.membership-section h4 {
  margin-bottom: 12px;
  color: #303133;
  font-size: 14px;
}

.membership-section {
  margin-top: 20px;
}

/* 响应式：小屏单列 */
@media (max-width: 768px) {
  .activity-body {
    flex-direction: column;
  }

  .activity-side {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }

  .activity-cover {
    width: 100px;
    height: 76px;
  }

  .activity-meta {
    grid-template-columns: 1fr;
  }
}
</style>
